/**
 * Owner -> Database backups: one button dumps the whole platform database,
 * encrypted to the owner's public key before it is written.
 *
 * Distinct from `DeletionBackupsPage`, which lists the per-tenant escrow
 * snapshot taken when a hospital is purged. This is the whole platform, on
 * demand. Neither page can decrypt anything — the private key never reaches a
 * server, so decryption only ever happens on the operator's own machine.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { visibleInterval } from '@/lib/polling';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { DataTable } from '@/components/ui/layout';
import { Button, StatusBadge } from '@/components/ui/primitives';

interface BackupListItem {
  id: string;
  createdAt: string;
  sizeBytes: number;
  status: 'running' | 'ready' | 'failed';
  error?: string;
}

interface BackupManifest {
  id: string;
  createdAt: string;
  sizeBytes: number;
  encryptedKey: string;
  iv: string;
  authTag: string;
}

interface DownloadLink {
  url: string;
  expiresAt: string;
}

type PendingAction = { kind: 'start' } | { kind: 'delete'; id: string } | { kind: 'download'; id: string };

function formatSize(bytes: number) {
  if (bytes <= 0) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function DatabaseBackupsPage() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['owner', 'db-backups'],
    queryFn: () => api<BackupListItem[]>('/platform/backups'),
    refetchInterval: (q) => ((q.state.data ?? []).some((b) => b.status === 'running') ? visibleInterval(10_000)() : false),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['owner', 'db-backups'] });

  const rows = query.data ?? [];
  const running = rows.some((b) => b.status === 'running');

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Database backups</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            A dump of the whole platform database, encrypted to your backup key before it is
            written.
          </p>
        </div>
        <button type="button" className="btn-primary" disabled={running} onClick={() => setPending({ kind: 'start' })}>
          {running ? 'Backup running…' : 'Take a backup now'}
        </button>
      </div>

      {message ? (
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm dark:bg-slate-800">{message}</p>
      ) : null}

      <p className="text-xs leading-6 text-slate-500 dark:text-slate-400">
        Each backup downloads as two files — the <code>.bin</code> is the encrypted dump, the{' '}
        <code>.json</code> holds the decryption key wrapped with your public key. Keep them
        together. Patient documents live in object storage and are covered by
        <code> deploy/scripts/backup.sh</code>, not by this button.
      </p>

      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        empty="No backups yet."
        rowKey={(b) => b.id}
        columns={[
          { header: 'Backup', sort: (b) => b.id, cell: (b) => <span className="font-mono text-xs">{b.id}</span> },
          { header: 'Taken on', sort: (b) => b.createdAt, cell: (b) => new Date(b.createdAt).toLocaleString() },
          { header: 'Size', numeric: true, sort: (b) => b.sizeBytes, cell: (b) => formatSize(b.sizeBytes) },
          {
            header: 'Status',
            sort: (b) => b.status,
            cell: (b) => (
              <span title={b.status === 'failed' ? b.error : undefined}>
                <StatusBadge status={b.status} tone={b.status === 'running' ? 'warning' : b.status === 'failed' ? 'destructive' : 'success'} />
              </span>
            ),
          },
        ]}
        rowActions={(b) =>
          b.status === 'ready' ? (
            <>
              <Button type="button" size="xs" variant="ghost" onClick={() => setPending({ kind: 'download', id: b.id })}>
                Download
              </Button>
              <Button type="button" size="xs" variant="danger-outline" onClick={() => setPending({ kind: 'delete', id: b.id })}>
                Delete
              </Button>
            </>
          ) : null
        }
      />

      {pending?.kind === 'start' && (
        <OwnerConfirmDialog
          open
          onClose={() => setPending(null)}
          title="Take a database backup"
          description="A full encrypted dump of every facility."
          confirmLabel="Start backup"
          action={(payload) => api<{ id: string }>('/platform/backups', { method: 'POST', body: payload })}
          onDone={() => {
            setPending(null);
            setMessage('Backup started — it keeps running if you leave this page.');
            refresh();
          }}
        />
      )}
      {pending?.kind === 'delete' && (
        <OwnerConfirmDialog
          open
          destructive
          onClose={() => setPending(null)}
          title="Delete this backup"
          description={pending.id}
          confirmLabel="Delete backup"
          action={(payload) => api(`/platform/backups/${pending.id}`, { method: 'DELETE', body: payload })}
          onDone={() => {
            setPending(null);
            setMessage('Backup deleted');
            refresh();
          }}
        />
      )}
      {pending?.kind === 'download' && (
        <OwnerConfirmDialog<DownloadLink>
          open
          onClose={() => setPending(null)}
          title="Download this backup"
          description="Both files are saved; the link for the dump works once, for 60 seconds."
          confirmLabel="Download"
          action={async (payload) => {
            const manifest = await api<BackupManifest>(`/platform/backups/${pending.id}/manifest`);
            saveBlob(new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' }), `${pending.id}.json`);
            return api<DownloadLink>(`/platform/backups/${pending.id}/link`, { method: 'POST', body: payload });
          }}
          onDone={(link) => {
            setPending(null);
            window.location.assign(link.url);
            setMessage('Both files saved — the .bin is useless without the .json.');
          }}
        />
      )}
    </div>
  );
}
